import { ReactNode, useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import LinkButton from '@/components/ui/link-button'
import { cn } from '@/lib/utils'

type Props = {
  name: string
  prefix?: ReactNode
  editPath?: string
  canEdit: boolean
  onChange?: () => void
  onUnlink?: () => void
  onMoveUp?: () => void
  onMoveDown?: () => void
  canMoveUp?: boolean
  canMoveDown?: boolean
  extraActions?: ReactNode
  defaultExpanded?: boolean
  children?: ReactNode
}

const EventActivityLinkedPlanTitleRow = ({
  name,
  prefix,
  editPath,
  canEdit,
  onChange,
  onUnlink,
  onMoveUp,
  onMoveDown,
  canMoveUp = false,
  canMoveDown = false,
  extraActions,
  defaultExpanded = true,
  children,
}: Props) => {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const showReorder = canEdit && (onMoveUp || onMoveDown)

  const toggleExpanded = () => setExpanded((value) => !value)

  return (
    <div className='first:mt-4'>
      <div
        className={cn(
          '-mx-4 flex items-center justify-between gap-2 border-t border-border px-4 pt-3 flex-wrap',
          expanded ? 'pb-0' : 'pb-3',
        )}
      >
        <button
          type='button'
          onClick={toggleExpanded}
          className='group/title flex min-w-48 flex-1 items-center gap-1.5 text-left cursor-pointer'
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse plan' : 'Expand plan'}
        >
          <ChevronDown
            className={cn(
              'h-4 w-4 shrink-0 text-muted-foreground transition-[color,transform] group-hover/title:text-accent-foreground',
              expanded ? '' : '-rotate-90',
            )}
          />
          {prefix}
          {prefix ? (
            <span className='min-w-0 truncate text-sm text-muted-foreground transition-colors group-hover/title:text-accent-foreground'>
              {name}
            </span>
          ) : (
            <h4 className='m-0 min-w-0 truncate transition-colors group-hover/title:text-accent-foreground'>
              {name}
            </h4>
          )}
        </button>
        <div className='flex items-center gap-2 flex-wrap justify-end'>
          {extraActions}
          {canEdit && editPath ? (
            <LinkButton size='xs' variant='outline' to={editPath}>
              Edit base plan
            </LinkButton>
          ) : null}
          {canEdit && onChange ? (
            <Button size='xs' variant='outline' onClick={onChange}>
              Change
            </Button>
          ) : null}
          {canEdit && onUnlink ? (
            <Button size='xs' variant='outline' onClick={onUnlink}>
              Unlink
            </Button>
          ) : null}
          {showReorder ? (
            <>
              <Button
                type='button'
                size='xs'
                variant='outline'
                disabled={!canMoveUp}
                onClick={onMoveUp}
                aria-label='Move plan up'
              >
                <ChevronUp className='h-3.5 w-3.5' />
              </Button>
              <Button
                type='button'
                size='xs'
                variant='outline'
                disabled={!canMoveDown}
                onClick={onMoveDown}
                aria-label='Move plan down'
              >
                <ChevronDown className='h-3.5 w-3.5' />
              </Button>
            </>
          ) : null}
        </div>
      </div>
      {expanded ? <div className='pt-3 pb-4'>{children}</div> : null}
    </div>
  )
}

export default EventActivityLinkedPlanTitleRow
